import { $HandrailEnd } from '../__generated/enhanced-model.js';
import { HandrailEndType, HandrailType, PositionType, PostStainlessFinishType, PostType } from '../__generated/model/index.js';
import { PartsBindingBuilder } from '../sdk/data-binding.js';
import { Materials } from './materials.js';
import { ObjectNames, getObjectInfo } from './objects.js';
const AngleRotatedObjects = [
    ObjectNames.roundAdjustableCorner,
    ObjectNames.squareAdjustableCorner,
    ObjectNames.roundAdjustableStairCornerRotatable,
    ObjectNames.squareAdjustableStairCornerRotatable
];
const parts = PartsBindingBuilder.from('handrailParts', $HandrailEnd, [
    '$postType',
    '$handrailType',
    '$postFinish',
    'positionType',
    'endType'
], (data, s)=>{
    const parts = [
        {
            skus: data.skus.map((sku)=>({
                    sku,
                    metadata: {}
                })),
            objects: data.objectNames?.map((object)=>{
                const offsetZ = data.offsetZ ?? 0;
                const rotationX = data.rotationX ?? 0;
                const rotationY = data.rotationY ?? 0;
                const rotationZ = data.rotationZ ?? 0;
                const objectInfo = getObjectInfo(object);
                const defaultObject = {
                    key: `${s.key}_${object}`,
                    x: s.x,
                    y: s.y,
                    z: s.z + offsetZ,
                    fileUrl: objectInfo.fileUrl,
                    objectName: object,
                    material: data.material,
                    rotationX,
                    rotationY,
                    rotationZ
                };
                if (AngleRotatedObjects.includes(object) && data.rotateInPlane) {
                    defaultObject.rotationY = rotationY + Math.PI - s.angle;
                }
                if (data.sloped === true) {
                    defaultObject.rotationX = rotationX + s.slope;
                }
                if (AngleRotatedObjects.includes(object) && data.slopeRotatable === 'direct') {
                    defaultObject.rotationX = rotationX + s.slope;
                }
                if (AngleRotatedObjects.includes(object) && data.slopeRotatable === 'indirect') {
                    defaultObject.rotationX = rotationX - s.slope;
                }
                if (AngleRotatedObjects.includes(object) && data.slopeRotatable === 'reverse-down') {
                    defaultObject.rotationY = rotationY - s.slope;
                }
                if (AngleRotatedObjects.includes(object) && data.slopeRotatable === 'reverse-up') {
                    defaultObject.rotationY = rotationY + s.slope;
                }
                return defaultObject;
            })
        }
    ];
    return parts;
}, [
    // #region Timber post type
    {
        keys: {
            $postType: PostType.Timber,
            $handrailType: HandrailType.values(),
            $postFinish: PostStainlessFinishType.values(),
            positionType: PositionType.values(),
            endType: HandrailEndType.values()
        },
        values: {
            skus: [],
            objectNames: [],
            material: ''
        }
    },
    // #endregion
    // Metal post type
    // #region Timber handrail
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.FlatTimber,
            $postFinish: PostStainlessFinishType.values(),
            positionType: PositionType.values(),
            endType: HandrailEndType.values()
        },
        values: {
            skus: [],
            objectNames: [],
            material: ''
        }
    },
    // #endregion
    // #region Round Stainless
    // FlatFlatRight
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2002E'
            ],
            objectNames: [
                ObjectNames.roundEndElbow
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2002E'
            ],
            objectNames: [
                ObjectNames.roundEndElbow
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2
        }
    },
    // Adjustable corner
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatObtuse,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotateInPlane: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatObtuse,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotateInPlane: true
        }
    },
    // Adjustable corner straight stairs
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-down'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-down'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-up'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-up'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20350E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableCornerRotatable,
                ObjectNames.roundAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    // Adjustable corner joiner - stairs right angle corners
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: -Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: -Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'indirect'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'indirect'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: -55,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-20351E'
            ],
            objectNames: [
                ObjectNames.roundAdjustableStairCornerRotatable,
                ObjectNames.roundAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: -55,
            slopeRotatable: 'direct'
        }
    },
    // Free end
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatAcuteLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatAcuteRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatAcuteLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatAcuteRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // Free stairs end
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsTopLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsTopLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsBottomLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsBottomLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsTopRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsTopRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsBottomRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsBottomRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    // Possible wall end
    // Flat end cap
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.FlatEndCap
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.FlatEndCap
        },
        values: {
            skus: [
                'HR-S-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.FlatEndCap
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.FlatEndCap
        },
        values: {
            skus: [
                'HR-2019A'
            ],
            objectNames: [
                ObjectNames.roundFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // Radiused end cap
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.RadiusedEndCap
        },
        values: {
            skus: [
                'HR-S-2017A'
            ],
            objectNames: [
                ObjectNames.roundRadiusedEndCap
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.RadiusedEndCap
        },
        values: {
            skus: [
                'HR-S-2017A'
            ],
            objectNames: [
                ObjectNames.roundRadiusedEndCap
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.RadiusedEndCap
        },
        values: {
            skus: [
                'HR-2017A'
            ],
            objectNames: [
                ObjectNames.roundRadiusedEndCap
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.RadiusedEndCap
        },
        values: {
            skus: [
                'HR-2017A'
            ],
            objectNames: [
                ObjectNames.roundRadiusedEndCap
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // Domed end cap
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.DomedEndCap
        },
        values: {
            skus: [
                'HR-S-2018A'
            ],
            objectNames: [
                ObjectNames.roundDomedEndCap
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.DomedEndCap
        },
        values: {
            skus: [
                'HR-S-2018A'
            ],
            objectNames: [
                ObjectNames.roundDomedEndCap
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.DomedEndCap
        },
        values: {
            skus: [
                'HR-2018A'
            ],
            objectNames: [
                ObjectNames.roundDomedEndCap
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.DomedEndCap
        },
        values: {
            skus: [
                'HR-2018A'
            ],
            objectNames: [
                ObjectNames.roundDomedEndCap
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // Round Wall Flange
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-S-2016A'
            ],
            objectNames: [
                ObjectNames.roundWallPlate
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-S-2016A'
            ],
            objectNames: [
                ObjectNames.roundWallPlate
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-2016A'
            ],
            objectNames: [
                ObjectNames.roundWallPlate
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-2016A'
            ],
            objectNames: [
                ObjectNames.roundWallPlate
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // Oblong Wall Plate
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-S-2014A'
            ],
            objectNames: [
                ObjectNames.roundOblongWallPlate
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-S-2014A'
            ],
            objectNames: [
                ObjectNames.roundOblongWallPlate
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-2014A'
            ],
            objectNames: [
                ObjectNames.roundOblongWallPlate
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.RoundStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-2014A'
            ],
            objectNames: [
                ObjectNames.roundOblongWallPlate
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // #endregion
    // #region Square Stainless
    // FlatFlatRight
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2002'
            ],
            objectNames: [
                ObjectNames.squareElbow
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2002'
            ],
            objectNames: [
                ObjectNames.squareElbow
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    // Adjustable Corner
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatObtuse,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotateInPlane: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatObtuse,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotateInPlane: true
        }
    },
    // Adjustable corner straight stairs
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-down'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-down'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-up'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            slopeRotatable: 'reverse-up'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsStraightBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsStraightBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20350E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableCornerRotatable,
                ObjectNames.squareAdjustableCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2,
            rotationZ: -Math.PI / 2,
            slopeRotatable: 'direct'
        }
    },
    // Adjustable corner joiner - stairs right angle corners
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: -Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: -Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'indirect'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'indirect'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: 60,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsCornerBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalSatin,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: -55,
            slopeRotatable: 'direct'
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsCornerBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-20351E'
            ],
            objectNames: [
                ObjectNames.squareAdjustableStairCornerRotatable,
                ObjectNames.squareAdjustableStairCorner
            ],
            material: Materials.MetalMirrorPolish,
            rotationX: -Math.PI / 2,
            rotationY: 0,
            rotationZ: Math.PI / 2,
            offsetZ: -55,
            slopeRotatable: 'direct'
        }
    },
    // Free end
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatAcuteLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatFlatAcuteRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatAcuteLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatFlatAcuteRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.FlatStairsOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.FlatStairsOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    // Free Stairs end
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsTopLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsTopLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsBottomLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsBottomLeftEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsTopRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsTopRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsBottomRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsBottomRightEnd,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherTopLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherBottomLeft,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherTopRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.StairsFlatOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.StairsFlatOtherBottomRight,
            endType: HandrailEndType.values()
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: -Math.PI / 2,
            sloped: true
        }
    },
    // Possible wall end
    // Flat end cap (all possible free end types resolve to this one)
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: [
                HandrailEndType.FlatEndCap,
                HandrailEndType.RadiusedEndCap,
                HandrailEndType.DomedEndCap
            ]
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: [
                HandrailEndType.FlatEndCap,
                HandrailEndType.RadiusedEndCap,
                HandrailEndType.DomedEndCap
            ]
        },
        values: {
            skus: [
                'HR-SS-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalSatin,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: [
                HandrailEndType.FlatEndCap,
                HandrailEndType.RadiusedEndCap,
                HandrailEndType.DomedEndCap
            ]
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: [
                HandrailEndType.FlatEndCap,
                HandrailEndType.RadiusedEndCap,
                HandrailEndType.DomedEndCap
            ]
        },
        values: {
            skus: [
                'HR-SM-2019'
            ],
            objectNames: [
                ObjectNames.squareFreeEnd
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: 0
        }
    },
    // Round Wall Flange
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-SS-2016'
            ],
            objectNames: [
                ObjectNames.squareWallPlate
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-SS-2016'
            ],
            objectNames: [
                ObjectNames.squareWallPlate
            ],
            material: Materials.MetalSatin,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-SM-2016'
            ],
            objectNames: [
                ObjectNames.squareWallPlate
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.RoundWallPlate
        },
        values: {
            skus: [
                'HR-SM-2016'
            ],
            objectNames: [
                ObjectNames.squareWallPlate
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: 0
        }
    },
    // Oblong Wall Plate
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-SS-2014'
            ],
            objectNames: [
                ObjectNames.squareOblongWallPlate
            ],
            material: Materials.MetalSatin,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Satin,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-SS-2014'
            ],
            objectNames: [
                ObjectNames.squareOblongWallPlate
            ],
            material: Materials.MetalSatin,
            rotationY: 0
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.LeftEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-SM-2014'
            ],
            objectNames: [
                ObjectNames.squareOblongWallPlate
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: Math.PI
        }
    },
    {
        keys: {
            $postType: PostType.Metal,
            $handrailType: HandrailType.SquareStainless,
            $postFinish: PostStainlessFinishType.Mirror,
            positionType: PositionType.RightEnd,
            endType: HandrailEndType.OblongWallPlate
        },
        values: {
            skus: [
                'HR-SM-2014'
            ],
            objectNames: [
                ObjectNames.squareOblongWallPlate
            ],
            material: Materials.MetalMirrorPolish,
            rotationY: 0
        }
    }
]);
export const HandrailEndBindings = {
    parts
};

//# sourceMappingURL=handrail-end.js.map